import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { formatDate as formatDateUtil } from '../utils/dateUtils';
import SpaceBackground from '../components/layout/SpaceBackground';
import {
  ClipboardList,
  Plus,
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  User,
  Briefcase,
  Filter,
  Search,
  MoreVertical,
  Check,
  RotateCcw,
  Trash2,
  Edit
} from 'lucide-react';

const Triage = () => {
  const { user } = useAuth();
  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [myTasksOnly, setMyTasksOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAction, setEditingAction] = useState(null);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [actionsRes, statsRes] = await Promise.all([
        api.get(`/triage/?status=${filter}`),
        api.get('/triage/stats')
      ]);
      setActions(actionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error loading triage data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current user's display name for matching
  const currentUserName = user?.display_name || user?.full_name || user?.username || '';

  // Check if action is assigned to current user
  const isMyTask = (action) => {
    if (!currentUserName) return false;
    return action.assigned_to?.toLowerCase() === currentUserName.toLowerCase();
  };

  // Count my tasks
  const myTasksCount = actions.filter(a => isMyTask(a) && a.completed === 0).length;

  const handleComplete = async (actionId) => {
    try {
      await api.patch(`/triage/${actionId}/complete`);
      loadData();
    } catch (error) {
      console.error('Error completing action:', error);
    }
  };

  const handleReopen = async (actionId) => {
    try {
      await api.patch(`/triage/${actionId}/reopen`);
      loadData();
    } catch (error) {
      console.error('Error reopening action:', error);
    }
  };

  const handleDelete = async (actionId) => {
    if (!window.confirm('Are you sure you want to delete this action?')) return;
    try {
      await api.delete(`/triage/${actionId}`);
      loadData();
    } catch (error) {
      console.error('Error deleting action:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDateUtil(dateStr);
  };

  const isOverdue = (action) => {
    if (action.completed === 1) return false;
    return new Date(action.due_date) < new Date();
  };

  const isDueToday = (action) => {
    if (action.completed === 1) return false;
    const today = new Date();
    const dueDate = new Date(action.due_date);
    return dueDate.toDateString() === today.toDateString();
  };

  const filteredActions = actions.filter(action => {
    // Filter by myTasksOnly
    if (myTasksOnly && !isMyTask(action)) return false;

    // Filter by search term
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      action.case_name?.toLowerCase().includes(search) ||
      action.client_name?.toLowerCase().includes(search) ||
      action.action_description?.toLowerCase().includes(search) ||
      action.assigned_to?.toLowerCase().includes(search)
    );
  });

  const StatCard = ({ icon: Icon, label, value, color, onClick, active }) => (
    <button
      onClick={onClick}
      className={`bg-virgo-navy-700 rounded-lg shadow p-4 flex items-center space-x-4 transition hover:shadow-md ${
        active ? 'ring-2 ring-blue-500' : ''
      }`}
    >
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="text-white" size={24} />
      </div>
      <div className="text-left">
        <p className="text-2xl font-bold text-virgo-gold-400">{value}</p>
        <p className="text-sm text-virgo-teal-400">{label}</p>
      </div>
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto relative">
      <SpaceBackground />
      <div className="relative z-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-virgo-teal-100 flex items-center">
          <ClipboardList className="mr-3" size={32} />
          Triage
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-virgo-teal-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-virgo-teal-700 transition flex items-center"
        >
          <Plus size={20} className="mr-2" />
          New Action
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <StatCard
            icon={User}
            label="My Tasks"
            value={myTasksCount}
            color="bg-blue-500"
            onClick={() => {
              setMyTasksOnly(!myTasksOnly);
              if (!myTasksOnly) setFilter('pending');
            }}
            active={myTasksOnly}
          />
          <StatCard
            icon={ClipboardList}
            label="Total"
            value={stats.total}
            color="bg-gray-500"
            onClick={() => { setFilter(''); setMyTasksOnly(false); }}
            active={filter === '' && !myTasksOnly}
          />
          <StatCard
            icon={Clock}
            label="Pending"
            value={stats.pending}
            color="bg-virgo-teal-500"
            onClick={() => { setFilter('pending'); setMyTasksOnly(false); }}
            active={filter === 'pending' && !myTasksOnly}
          />
          <StatCard
            icon={CheckCircle}
            label="Completed"
            value={stats.completed}
            color="bg-green-500"
            onClick={() => { setFilter('completed'); setMyTasksOnly(false); }}
            active={filter === 'completed' && !myTasksOnly}
          />
          <StatCard
            icon={AlertTriangle}
            label="Overdue"
            value={stats.overdue}
            color="bg-red-500"
            onClick={() => { setFilter('overdue'); setMyTasksOnly(false); }}
            active={filter === 'overdue' && !myTasksOnly}
          />
          <StatCard
            icon={Calendar}
            label="Due Today"
            value={stats.due_today}
            color="bg-orange-500"
            onClick={() => { setFilter('due_today'); setMyTasksOnly(false); }}
            active={filter === 'due_today' && !myTasksOnly}
          />
          <StatCard
            icon={Calendar}
            label="This Week"
            value={stats.due_this_week}
            color="bg-cyan-500"
            onClick={() => { setFilter('due_this_week'); setMyTasksOnly(false); }}
            active={filter === 'due_this_week' && !myTasksOnly}
          />
        </div>
      )}

      {/* Search */}
      <div className="bg-virgo-navy-700 rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-virgo-teal-500" size={20} />
          <input
            type="text"
            placeholder="Search actions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 placeholder-virgo-teal-500"
          />
        </div>
      </div>

      {/* Actions List */}
      <div className="bg-virgo-navy-700 rounded-lg shadow">
        {loading ? (
          <div className="p-8 text-center text-virgo-teal-400">Loading...</div>
        ) : filteredActions.length === 0 ? (
          <div className="p-8 text-center text-virgo-teal-400">
            No triage actions found
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredActions.map((action) => (
              <div
                key={action.id}
                className={`p-4 hover:bg-virgo-navy-600 transition ${
                  action.completed === 1 ? 'opacity-60' : ''
                } ${isMyTask(action) && action.completed !== 1 ? 'border-l-4 border-blue-500 bg-blue-900/10' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {action.completed === 1 ? (
                        <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
                      ) : isOverdue(action) ? (
                        <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />
                      ) : isDueToday(action) ? (
                        <Clock className="text-orange-500 flex-shrink-0" size={20} />
                      ) : (
                        <Clock className="text-virgo-teal-300 flex-shrink-0" size={20} />
                      )}
                      <h3 className={`font-semibold text-white ${
                        action.completed === 1 ? 'line-through' : ''
                      }`}>
                        {action.action_description}
                      </h3>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-virgo-teal-300 ml-8">
                      {(action.client_name || action.case_name) && (
                        <span className="flex items-center">
                          <Briefcase size={14} className="mr-1" />
                          {action.client_name ? `${action.client_name} - ${action.case_name}` : action.case_name}
                        </span>
                      )}
                      <span className="flex items-center">
                        <User size={14} className="mr-1" />
                        {action.assigned_to}
                      </span>
                      <span className={`flex items-center ${
                        isOverdue(action) ? 'text-red-600 font-semibold' : ''
                      }`}>
                        <Calendar size={14} className="mr-1" />
                        Due: {formatDate(action.due_date)}
                      </span>
                      {action.next_court_date && (
                        <span className="flex items-center text-cyan-600">
                          <Calendar size={14} className="mr-1" />
                          Court: {formatDate(action.next_court_date)}
                        </span>
                      )}
                    </div>
                    
                    {action.notes && (
                      <p className="text-sm text-virgo-teal-400 mt-2 ml-8">{action.notes}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    {action.completed === 0 ? (
                      <button
                        onClick={() => handleComplete(action.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                        title="Mark Complete"
                      >
                        <Check size={18} />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReopen(action.id)}
                        className="p-2 text-virgo-teal-400 hover:bg-blue-50 rounded-lg transition"
                        title="Reopen"
                      >
                        <RotateCcw size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => setEditingAction(action)}
                      className="p-2 text-virgo-teal-300 hover:bg-virgo-navy-800 rounded-lg transition"
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(action.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingAction) && (
        <TriageModal
          action={editingAction}
          onClose={() => {
            setShowCreateModal(false);
            setEditingAction(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingAction(null);
            loadData();
          }}
        />
      )}
      </div>
    </div>
  );
};

// Triage Modal Component
const TriageModal = ({ action, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    case_name: action?.case_name || '',
    client_name: action?.client_name || '',
    action_description: action?.action_description || '',
    assigned_to: action?.assigned_to || '',
    due_date: action?.due_date ? action.due_date.slice(0, 10) : '',  // Just the date, time will be set to COB
    next_court_date: action?.next_court_date ? action.next_court_date.slice(0, 16) : '',
    hearing_type: action?.hearing_type || '',
    notes: action?.notes || '',
    virgo_case_id: action?.virgo_case_id || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [openCases, setOpenCases] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, casesRes] = await Promise.all([
          api.get('/cases/assignable-users/list'),
          api.get('/triage/cases/open')
        ]);
        setUsers(usersRes.data.all || []);
        setOpenCases(casesRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Set due date to close of business (5:00 PM) on the selected date
      const dueDateWithCOB = new Date(`${formData.due_date}T17:00:00`);

      const payload = {
        ...formData,
        due_date: dueDateWithCOB.toISOString(),
        next_court_date: formData.next_court_date
          ? new Date(formData.next_court_date).toISOString()
          : null,
        virgo_case_id: formData.virgo_case_id ? formData.virgo_case_id : null,
        case_name: formData.case_name || 'Firm Task',  // Default for standalone tasks
        client_name: formData.client_name || null
      };

      if (action) {
        await api.put(`/triage/${action.id}`, payload);
      } else {
        await api.post('/triage/', payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save action');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-virgo-navy-700 rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-virgo-gold-400">
            {action ? 'Edit Action' : 'New Triage Action'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Action Description *
            </label>
            <input
              type="text"
              value={formData.action_description}
              onChange={(e) => setFormData({ ...formData, action_description: e.target.value })}
              className="w-full px-3 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 placeholder-virgo-teal-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Case <span className="text-xs text-virgo-teal-500">(Optional - only open/pending cases)</span>
            </label>
            <select
              value={formData.virgo_case_id}
              onChange={(e) => {
                const selectedCase = openCases.find(c => c.id === e.target.value);
                setFormData({
                  ...formData,
                  virgo_case_id: e.target.value,
                  case_name: selectedCase?.case_name || '',
                  client_name: selectedCase?.client_name || ''
                });
              }}
              className="w-full px-3 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
            >
              <option value="">No case (standalone task)</option>
              {openCases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.client_name} - {c.case_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Assigned To *
            </label>
            <select
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="w-full px-3 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              required
            >
              <option value="">Select a user...</option>
              {users.map((user) => (
                <option key={user.id} value={user.display_name || user.username}>
                  {user.display_name || user.username} ({user.role})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
                Due Date * <span className="text-virgo-teal-500 text-xs">(COB 5:00 PM)</span>
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 [color-scheme:dark]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
                Court Date
              </label>
              <input
                type="datetime-local"
                value={formData.next_court_date}
                onChange={(e) => setFormData({ ...formData, next_court_date: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 [color-scheme:dark]"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Hearing Type
            </label>
            <input
              type="text"
              value={formData.hearing_type}
              onChange={(e) => setFormData({ ...formData, hearing_type: e.target.value })}
              className="w-full px-3 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 placeholder-virgo-teal-500"
              placeholder="e.g., Motion Hearing, Trial, Status Conference"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-200 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 bg-virgo-navy-800 text-white border border-virgo-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-virgo-teal-500 placeholder-virgo-teal-500"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-virgo-teal-200 bg-virgo-navy-800 rounded-lg hover:bg-virgo-navy-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Saving...' : (action ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Triage;
